/**
 * useFormDraft — przechowuje stan formularza w sessionStorage żeby zamknięcie
 * modala / drawera / nawigacja nie kasowała wpisanych danych.
 *
 * Użycie:
 *   const [form, setForm, clearForm] = useFormDraft('employee-create', EMPTY_FORM);
 *   // setForm({...form, name: 'Anna'}) — auto-persist do sessionStorage
 *   // clearForm() — np. po pomyślnym submit'cie żeby następne otwarcie zaczęło od pustego
 *
 * Per-tab persist (sessionStorage, nie localStorage) — drugi tab ma własny draft.
 *
 * Hydration na mount: stored ∪ initial (initial pokrywa nowe pola jeśli schema się
 * rozszerzyła; stored pokrywa pola wpisane przez użytkownika). JSON parse fail
 * albo prywatny tryb → fallback na initial bez błędu.
 */

import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';

const PREFIX = 'form-draft:';

function readStorage<T>(key: string, initial: T): T {
  if (typeof window === 'undefined') return initial;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return initial;
    const parsed = JSON.parse(raw) as Partial<T>;
    return { ...initial, ...parsed } as T;
  } catch {
    return initial;
  }
}

function writeStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* prywatny tryb / quota — silently ignore */
  }
}

function removeStorage(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* prywatny tryb */
  }
}

export function useFormDraft<T>(
  storageKey: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const fullKey = PREFIX + storageKey;

  const [state, setState] = useState<T>(() => readStorage(fullKey, initial));

  useEffect(() => {
    writeStorage(fullKey, state);
  }, [fullKey, state]);

  const clear = useCallback(() => {
    removeStorage(fullKey);
    setState(initial);
    // initial powinien byc stabilna referencja (typowo `EMPTY_FORM` const na poziomie
    // modulu) — react-hooks/exhaustive-deps wymusza zaleznosc, ale nowy initial
    // by przeladowal callback przy kazdym renderze. W praktyce uzywamy stabilnych const.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullKey]);

  return [state, setState, clear];
}
